import React from "react";
import {
  SECTION_CONTAINER_WIDTHS,
  type SectionContainerWidth,
} from "@/components/atoms/section-constants";
import { SectionBackground } from "../modules/Section/SectionBackground";
import { SectionContent } from "../modules/Section/SectionContent";
import { BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { isDarkColor } from "@/lib/colors";
import { cn } from "@/lib/utils";
import RichText from "../modules/RichText";
import { PortableTextBlock } from "@portabletext/types";
import { PortableTextComponentProps } from "next-sanity";
import { ContentImage, ContentVideo } from "@/content/types";
import { getPaddingClass } from "@/lib/spacing";
import { Box } from "@/components/atoms/Box";

type PortableContentBlockValue = {
  body: PortableTextBlock[];
  style?: {
    backgroundColor?: string;
    backgroundImage?: ContentImage;
    backgroundVideo?: ContentVideo;
    backgroundPattern?: string;
    patternOpacity?: number;
    textColor?: string;
    paddingTop?: string;
    paddingBottom?: string;
    containerWidth?: string;
    customClasses?: string;
  };
};

/**
 * PortableContentBlock Component
 *
 * A flexible interrupter block that can have its own background, padding, and patterns.
 * Useful for breaking up long text with highlighted sections.
 *
 * **Circular dependency:** This component imports RichText, which in turn registers
 * PortableContentBlock via a dynamic import (`next/dynamic`) to break the cycle at
 * runtime. This is a known architectural pattern, not a bug.
 *
 * @see docs/design-system-audit/03-dependency-analysis.md
 */
const PortableContentBlock = ({
  value,
}: PortableTextComponentProps<PortableContentBlockValue>) => {
  const { body, style } = value;

  const bgColor = style?.backgroundColor || "white";
  const paddingTop = style?.paddingTop || "pt-16";
  const paddingBottom = style?.paddingBottom || "pb-16";
  const containerWidthValue = style?.containerWidth || "max-w-7xl";
  const textColorSelection = style?.textColor || "auto";
  const customClasses = style?.customClasses || "";

  const ptClass = getPaddingClass(paddingTop, "pt-16");
  const pbClass = getPaddingClass(paddingBottom, "pb-16");
  const containerSize = (
    containerWidthValue in SECTION_CONTAINER_WIDTHS
      ? containerWidthValue
      : "max-w-7xl"
  ) as SectionContainerWidth;

  const isDark = isDarkColor(bgColor);

  let textColorClass = "";
  if (textColorSelection !== "auto") {
    textColorClass = textColorSelection;
  }

  return (
    // No `not-prose` on this wrapper: the typography plugin excludes all
    // descendants of a `not-prose` element, which would kill the nested
    // `prose` body below.
    <Box className="my-12">
      <SectionBackground
        aria-label="Content section"
        className={cn(
          "rounded-sm overflow-hidden",
          ptClass,
          pbClass,
          textColorClass,
          "relative transition-colors duration-200 ease-in-out",
          customClasses,
        )}
        backgroundColor={bgColor}
        backgroundImage={style?.backgroundImage}
        backgroundVideo={style?.backgroundVideo}
        backgroundPattern={style?.backgroundPattern as BackgroundPatternType}
        patternOpacity={style?.patternOpacity}
        isDark={isDark}
      >
        <SectionContent size={containerSize} className="h-full">
          <Box
            className={cn(
              "prose prose-lg md:prose-xl max-w-none",
              isDark ? "prose-invert" : "",
            )}
          >
            <RichText data={body} />
          </Box>
        </SectionContent>
      </SectionBackground>
    </Box>
  );
};

export default PortableContentBlock;
